<template>
  <div>
    <loader v-if="loading" />
    <div v-else>
      <h2 class="font-normal mb-6 px-1 text-lg"><strong>{{ form.properties_list.length | number }}</strong> included {{ form.properties_list.length === 1 ? 'property' : 'properties' }} across <strong>{{ pages.length | number }}</strong> {{ pages.length === 1 ? 'page' : 'pages' }}</h2>
      <div class="overflow-auto px-1">
        <table class="w-full">
          <thead>
            <tr class="border-t border-b">
              <th scope="col" colspan="2" class="text-left p-2">Property</th>
              <th scope="col" class="p-2">Department</th>
              <th scope="col" class="p-2">Price</th>
              <th scope="col" class="p-2">Status</th>
              <th scope="col" class="p-2">Listed At</th>
              <th scope="col" class="p-2">Front Cover</th>
              <th scope="col" class="p-2">Featured</th>
              <th scope="col" class="p-2">Included</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(page, i) in paginatedPages">
              <tr :key="`row${i}h1`">
                <th scope="rowgroup" colspan="9" class="text-left p-2" :class="{'pt-12': i > 0}">
                  <i class="fas fa-fw fa-file"></i> {{ page.name }} <small class="text-grey-dark font-normal ml-3">{{ page.selected | number }}/{{ page.total | number }} selected</small>
                </th>
              </tr>
              <tr v-if="page.properties.length === 0 || page.selected === 0" :key="`row${i}h2`">
                <td colspan="9" class="px-2" :class="page.selected === 0 ? 'pb-6' : null">
                  <i class="fas fa-exclamation-triangle text-yellow-dark"></i> {{ page.properties.length === 0 ? 'There are no available properties for this page.' : 'No properties are selected for this page.' }} <span class="text-blue hover:text-blue-dark underline cursor-pointer" @click="deleteModule(page.id)">Delete Page</span>
                </td>
              </tr>
              <tr v-for="property in page.properties" :key="property.id" class="border-b border-t" :class="{'bg-green-lightest': form.properties_list.includes(property.id)}">
                <td class="px-2 border-l-2" :class="{'border-green-dark': form.properties_list.includes(property.id)}">
                  <img v-if="property.main_image" :src="property.main_image.large_url" class="rounded w-16 h-10 mt-1 object-cover" />
                </td>
                <td class="p-2" :title="`Property ID: ${property.id}`">
                  {{ [[property.address_name_number, property.address_line2].filter(Boolean).join(' '), property.address_line3, property.address_town, property.address_line4, property.address_postcode].filter(Boolean).join(', ') }}
                </td>
                <td class="p-2 text-center">
                  {{ property.rental ? 'Lettings' : 'Sales' }}
                </td>
                <td class="p-2 text-center">
                  {{ getPrice(property) }}
                </td>
                <td class="p-2 text-center">
                  <span class="text-red-dark" v-if="property.deleted_at || !property.published"><i class="fas fa-exclamation-triangle"></i> Off-Market</span>
                  <span v-else>{{ getStatus(property.status_id) }}</span>
                </td>
                <td class="p-2 text-center">
                  {{ property.created_at | date }}
                </td>
                <td class="p-2 text-center">
                  <i class="fas fa-fw fa-certificate fa-2x" v-if="form.properties_list.includes(property.id)" :class="property.id === form.front_cover_property_id ? 'text-yellow-dark' : 'text-grey-light hover:text-grey cursor-pointer'" @click="setFrontCoverProperty(property.id)"></i>
                </td>
                <td class="text-center">
                  <i class="fas fa-fw fa-star fa-2x" v-if="form.properties_list.includes(property.id)" :class="form.featured_properties.includes(property.id) ? 'text-yellow-dark' : 'text-grey-light hover:text-grey cursor-pointer'" @click="toggleFeatured(property.id)"></i>
                </td>
                <td class="p-2 text-center border-r-2" :class="{'border-green-dark': form.properties_list.includes(property.id)}">
                  <input type="checkbox" class="green" :id="`property${property.id}`" :value="property.id" v-model="form.properties_list" :disabled="form.deleted_at" />
                  <label :for="`property${property.id}`">&nbsp;</label>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <paginator :data="{ current_page: page, last_page: totalPages }" @change="page = $event"/>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { v2Api } from '@/http'
import { filter, find, flatten, sortBy } from 'lodash'

export default {
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      form: this.value,
      loading: true,
      localities: null,
      properties: null,
      offices: null,
      orderBy: 'price',
      sort: 'desc',
      page: 1,
      perPage: 50
    }
  },
  computed: {
    pages () {
      return flatten(filter(this.form.pages, (page) => find(page.modules, (m) => m.module.component === 'properties-module')).map(page => page.modules)).map((m) => {
        const office = m.office_id ? find(this.offices, (office) => office.id === m.office_id) : null
        const properties = sortBy(filter(this.properties, (property) => (m.data.location ? find(this.localities, (locality) => locality.name === m.data.location && find(locality.postcode_sectors, (postcodeSector) => property.address_postcode.indexOf(postcodeSector) === 0)) : true) && (m.office_id ? property.office_id === m.office_id : true) && m.data.rental === property.rental), [this.orderBy])
        return {
          id: m.id,
          name: [office ? `${office.name} (${office.address_town})` : null, m.data.location, m.data.rental ? 'Lettings' : 'Sales'].filter(Boolean).join(' - '),
          properties,
          total: properties.length,
          selected: filter(properties, (property) => this.form.properties_list.includes(property.id)).length
        }
      })
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.pages.reduce((total, p) => total + p.total, 0) / this.perPage))
    },
    paginatedPages () {
      let output = []
      let startIndex = (this.page - 1) * this.perPage
      let endIndex = startIndex + this.perPage
      let index = -1

      for (let p = 0; p < this.pages.length; p++) {
        let item = { ...this.pages[p] }
        item.properties = []

        for (let i = 0; i < this.pages[p].properties.length; i++) {
          index++
          if (index < startIndex || index >= endIndex) {
            continue
          }

          item.properties.push(this.pages[p].properties[i])
        }

        if (index < startIndex) {
          continue
        }

        output.push(item)

        if (index >= endIndex) {
          break
        }
      }

      return output
    }
  },
  created () {
    this.getProperties()
  },
  methods: {
    getProperties () {
      v2Api.get(`ezine-editions/${this.form.id}/properties`).then((response) => {
        this.localities = response.data.localities
        this.properties = response.data.properties
        this.offices = response.data.offices
        this.$emit('offices-loaded', this.offices)
        this.loading = false
      })
    },
    getPrice (property) {
      let priceQualifier = null
      switch (property.price_qualifier_id) {
        case 1: priceQualifier = 'Asking Price '; break
        case 2: priceQualifier = 'Fixed Price '; break
        case 3: priceQualifier = 'Price from '; break
        case 4: priceQualifier = 'Guide Price '; break
        case 5: priceQualifier = 'Offers in region of '; break
        case 6: priceQualifier = 'Offers in excess of '; break
        case 7: priceQualifier = 'POA'; break
        case 8: priceQualifier = 'Sale by Tender '; break
        case 9: priceQualifier = 'Shared Ownership '; break
        case 10: priceQualifier = 'Auction Guide Price '; break
        case 12: priceQualifier = 'Part Buy Part Rent '; break
        case 13: priceQualifier = 'Shared Equity '; break
        case 14: priceQualifier = 'Offers Over '; break
        case 15: priceQualifier = 'From '
      }

      if (priceQualifier === 'POA') {
        return 'POA'
      }

      const price = Vue.filter('gbp')(property.price)
      let rentFrequency = null

      if (property.rental_data) {
        switch (property.rental_data.rent_frequency_id) {
          case 2: rentFrequency = 'pw'; break
          case 3: rentFrequency = 'pa'; break
          case 5: rentFrequency = 'pwps'; break
          case 6: rentFrequency = 'per day'; break
          default: rentFrequency = 'pcm'
        }
      }

      return [priceQualifier, price, rentFrequency].filter(Boolean).join('')
    },
    getStatus (id) {
      switch (id) {
        case 2: return 'SSTC'
        case 3: return 'SSTCM'
        case 4: return 'Under Offer'
        case 5: return 'Reserved'
        case 6: return 'Let Agreed'
        case 7: return 'Sold'
        case 8: return 'Let'
        case 9: return 'Exchanged'
        case 10: return 'Completed'
        case 11: return 'Withdrawn'
        default: return 'Available'
      }
    },
    deleteModule (id) {
      let page = find(this.form.pages, (page) => find(page.modules, (m) => m.id === id))
      if (!page) {
        return
      }

      const m = find(page.modules, (m) => m.id === id)
      page.modules.splice(page.modules.indexOf(m), 1)
      if (page.modules.length > 0) {
        return
      }
      this.form.pages.splice(this.form.pages.indexOf(page), 1)
    },
    setFrontCoverProperty (id) {
      if (this.form.deleted_at) {
        return
      }

      this.form.front_cover_property_id = id
    },
    toggleFeatured (id) {
      // Remove from featured properties if deselected
      this.form.featured_properties = this.form.featured_properties.filter((p) => this.form.properties_list.indexOf(p) >= 0)

      // Unlink from front cover if deselected
      const index = this.form.featured_properties.indexOf(id)
      if (index >= 0) {
        this.form.featured_properties.splice(index, 1)
        return
      }

      this.form.featured_properties.push(id)
    }
  },
  watch: {
    'form.properties_list' () {
      if (this.form.properties_list.includes(this.form.front_cover_property_id)) {
        return
      }

      this.form.front_cover_property_id = this.form.properties_list.length > 0 ? this.form.properties_list[0] : null
    },
    form: {
      handler () {
        this.$emit('input', this.form)
      },
      deep: true
    }
  }
}
</script>
